import { z } from 'zod';
import { WallClockSchema } from './attendance';
import { FileSummarySchema, PersonRefSchema, SiteRefSchema } from './property';

/**
 * Operations M1 (docs/specs/operations.md, D71): Operational Compliance (OC) — seven Autopart
 * reports per branch, read live from dt_spar's `oc_src_*` tables, with thresholds in Aroo,
 * daily history, Home tasks and RM escalation — and Announcements (admin-authored, targeted,
 * read/acknowledgement tracking). Dates are YYYY-MM-DD, instants ISO, and dates read from the
 * Autopart feed are London wall clock "YYYY-MM-DDTHH:MM:SS".
 */

const IsoDate = z.iso.date();
const Instant = z.iso.datetime();

export const OPERATIONS_PERMISSIONS = {
  complianceView: 'operations.compliance.view',
  complianceManage: 'operations.compliance.manage',
  announcementsManage: 'operations.announcements.manage',
} as const;

// Compliance: KPIs ------------------------------------------------------------------

export const OcStatusSchema = z.enum(['pass', 'amber', 'fail', 'nodata']);
export type OcStatus = z.infer<typeof OcStatusSchema>;
export const OC_STATUS_LABELS: Record<OcStatus, string> = {
  pass: 'Pass',
  amber: 'Pass (warning)',
  fail: 'Fail',
  nodata: 'No data',
};

export const OcWindowTypeSchema = z.enum(['none', 'rolling_days', 'since_year_start']);
export type OcWindowType = z.infer<typeof OcWindowTypeSchema>;

export const OcRuleSchema = z.enum([
  'suspended_invoices',
  'negative_stock',
  'goods_in_transit',
  'returns_not_credited',
  'returns_not_sent',
  'no_bin_location',
  'cod_unpaid',
]);
export type OcRule = z.infer<typeof OcRuleSchema>;

export const OcKpiSchema = z.object({
  code: z.string(),
  name: z.string(),
  description: z.string(),
  /** The standard in words, e.g. "Nothing over 3 days". */
  standard: z.string(),
  rule: OcRuleSchema,
  /** What one row is, e.g. "line", "part", "return", "invoice". */
  unit: z.string(),
  sortOrder: z.number().int(),
  windowType: OcWindowTypeSchema,
  windowDays: z.number().int().nullable(),
  minAgeDays: z.number().int().nullable(),
  minAgeWorkingDays: z.number().int().nullable(),
  suppliers: z.array(z.string()),
  supplierExcludes: z.array(z.string()),
  passMax: z.number().int(),
  amberMax: z.number().int().nullable(),
  countsToScore: z.boolean(),
  weight: z.number(),
  active: z.boolean(),
  /** Which settings apply to this rule (the editor shows only these). */
  uses: z.object({
    window: z.boolean(),
    minAgeDays: z.boolean(),
    minAgeWorkingDays: z.boolean(),
    suppliers: z.boolean(),
    supplierExcludes: z.boolean(),
  }),
  updatedBy: PersonRefSchema.nullable(),
  updatedAt: Instant.nullable(),
});
export type OcKpi = z.infer<typeof OcKpiSchema>;

export const OcKpiPatchSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    description: z.string().trim().max(1000),
    standard: z.string().trim().max(200),
    windowType: OcWindowTypeSchema,
    windowDays: z.number().int().min(1).max(366).nullable(),
    minAgeDays: z.number().int().min(0).max(365).nullable(),
    minAgeWorkingDays: z.number().int().min(0).max(60).nullable(),
    suppliers: z.array(z.string().trim().min(1).max(20)).max(100),
    supplierExcludes: z.array(z.string().trim().min(1).max(20)).max(500),
    passMax: z.number().int().min(0).max(100000),
    amberMax: z.number().int().min(0).max(100000).nullable(),
    countsToScore: z.boolean(),
    weight: z.number().min(0).max(100),
    active: z.boolean(),
  })
  .partial()
  .refine((p) => Object.keys(p).length > 0, 'Nothing to change');
export type OcKpiPatch = z.infer<typeof OcKpiPatchSchema>;

export const OcKpisResponseSchema = z.object({ kpis: z.array(OcKpiSchema) });
export type OcKpisResponse = z.infer<typeof OcKpisResponseSchema>;

export const OcSettingsSchema = z.object({
  escalateAfterDays: z.number().int(),
  staleMinutes: z.number().int(),
  snapshotTime: z.string(),
  updatedBy: PersonRefSchema.nullable(),
  updatedAt: Instant.nullable(),
  /** Recent threshold/settings changes (audit), newest first. */
  changes: z.array(
    z.object({
      at: Instant,
      actor: PersonRefSchema.nullable(),
      action: z.string(),
      subject: z.string(),
      before: z.record(z.string(), z.unknown()).nullable(),
      after: z.record(z.string(), z.unknown()).nullable(),
    }),
  ),
});
export type OcSettings = z.infer<typeof OcSettingsSchema>;

export const OcSettingsPatchSchema = z.object({
  escalateAfterDays: z.number().int().min(1).max(60).optional(),
  staleMinutes: z.number().int().min(15).max(1440).optional(),
});
export type OcSettingsPatch = z.infer<typeof OcSettingsPatchSchema>;

// Compliance: board -----------------------------------------------------------------

export const OcCellSchema = z.object({
  code: z.string(),
  count: z.number().int().nullable(),
  status: OcStatusSchema,
  stale: z.boolean(),
  /** Earliest row date (wall clock), when the rule has dates. */
  oldestAt: WallClockSchema.nullable(),
  /** Calendar days the KPI has been failing (0 on the first day), null when not failing. */
  failingDays: z.number().int().nullable(),
  failingSince: Instant.nullable(),
  escalated: z.boolean(),
});
export type OcCell = z.infer<typeof OcCellSchema>;

export const OcRegionRefSchema = z.object({ id: z.uuid(), code: z.string(), name: z.string() });
export type OcRegionRef = z.infer<typeof OcRegionRefSchema>;

export const OcBranchRowSchema = z.object({
  site: SiteRefSchema,
  region: OcRegionRefSchema,
  cells: z.array(OcCellSchema),
  /** 0–100, or null when no scoring KPI has data. */
  score: z.number().nullable(),
  passed: z.number().int(),
  scored: z.number().int(),
  failing: z.number().int(),
});
export type OcBranchRow = z.infer<typeof OcBranchRowSchema>;

export const OcDatasetSchema = z.object({
  dataset: z.string(),
  rowCount: z.number().int().nullable(),
  /** When the Pi last replaced it (wall clock), null when never. */
  syncedAt: WallClockSchema.nullable(),
  ageMinutes: z.number().int().nullable(),
  stale: z.boolean(),
  lastError: z.string().nullable(),
});
export type OcDataset = z.infer<typeof OcDatasetSchema>;

/**
 * Plain names for the Autopart feed's datasets (the Pi's `oc_src_*` snapshots), in the order the
 * feed health panel lists them. Heads are documents, lines their rows.
 */
export const OC_DATASET_LABELS: Readonly<Record<string, string>> = {
  iheads: 'Invoices',
  customer: 'Customer accounts',
  sledger: 'Account balances',
  stock: 'Stock',
  uheads: 'Suspended invoices',
  ulines: 'Suspended invoice lines',
  pheads: 'Purchase orders',
  plines: 'Purchase order lines',
  nheads: 'Supplier returns',
  nlines: 'Supplier return lines',
  nsurch: 'Return surcharges',
};

/** "Invoices" for `iheads` (any case, with or without `oc_src_`); unknown names title-cased. */
export function ocDatasetLabel(dataset: string): string {
  const key = dataset.toLowerCase().replace(/^oc_src_/, '');
  const known = OC_DATASET_LABELS[key];
  if (known) return known;
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : dataset;
}

/** Datasets in the panel's order (known ones first, as listed above), then any others A–Z. */
export function ocDatasetOrder(a: string, b: string): number {
  const keys = Object.keys(OC_DATASET_LABELS);
  const ia = keys.indexOf(a.toLowerCase().replace(/^oc_src_/, ''));
  const ib = keys.indexOf(b.toLowerCase().replace(/^oc_src_/, ''));
  if (ia >= 0 || ib >= 0) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  return a.localeCompare(b);
}

export const OcSyncHealthSchema = z.object({
  computedAt: Instant.nullable(),
  lastOkAt: Instant.nullable(),
  lastError: z.string().nullable(),
  datasets: z.array(OcDatasetSchema),
});
export type OcSyncHealth = z.infer<typeof OcSyncHealthSchema>;

export const OcRegionSummarySchema = z.object({
  region: OcRegionRefSchema,
  branches: z.number().int(),
  averageScore: z.number().nullable(),
  fullMarks: z.number().int(),
  failingCells: z.number().int(),
});
export type OcRegionSummary = z.infer<typeof OcRegionSummarySchema>;

export const OcBoardSchema = z.object({
  kpis: z.array(OcKpiSchema),
  branches: z.array(OcBranchRowSchema),
  regions: z.array(OcRegionSummarySchema),
  totals: z.object({
    branches: z.number().int(),
    averageScore: z.number().nullable(),
    fullMarks: z.number().int(),
    failingCells: z.number().int(),
  }),
  sync: OcSyncHealthSchema,
  can: z.object({ manage: z.boolean() }),
});
export type OcBoard = z.infer<typeof OcBoardSchema>;

// Compliance: history ---------------------------------------------------------------

export const OcHistoryDaySchema = z.object({
  date: IsoDate,
  /** Weighted score 0–100 (branch) or the average over branches (region/company). */
  score: z.number().nullable(),
  /** Per KPI code: the count (branch) or the summed count (region). */
  counts: z.record(z.string(), z.number().int().nullable()),
  /** Per KPI code: how many branches failed it that day (1/0 for a branch). */
  failing: z.record(z.string(), z.number().int()),
});
export type OcHistoryDay = z.infer<typeof OcHistoryDaySchema>;

export const OcHistorySchema = z.object({
  from: IsoDate,
  to: IsoDate,
  days: z.array(OcHistoryDaySchema),
});
export type OcHistory = z.infer<typeof OcHistorySchema>;

export const OcBranchDetailSchema = z.object({
  row: OcBranchRowSchema,
  kpis: z.array(OcKpiSchema),
  history: OcHistorySchema,
  sync: OcSyncHealthSchema,
});
export type OcBranchDetail = z.infer<typeof OcBranchDetailSchema>;

export const OcRegionDetailSchema = z.object({
  region: OcRegionRefSchema,
  kpis: z.array(OcKpiSchema),
  branches: z.array(OcBranchRowSchema),
  summary: OcRegionSummarySchema,
  history: OcHistorySchema,
  /** Per branch score history, for small multiples. */
  branchHistory: z.array(
    z.object({
      siteId: z.uuid(),
      days: z.array(z.object({ date: IsoDate, score: z.number().nullable() })),
    }),
  ),
});
export type OcRegionDetail = z.infer<typeof OcRegionDetailSchema>;

// Compliance: drill-down ------------------------------------------------------------

export const OcDrillColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum(['text', 'number', 'datetime']),
});
export type OcDrillColumn = z.infer<typeof OcDrillColumnSchema>;

export const OcDrillDownSchema = z.object({
  site: SiteRefSchema,
  kpi: OcKpiSchema,
  status: OcStatusSchema,
  count: z.number().int(),
  columns: z.array(OcDrillColumnSchema),
  rows: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.null()]))),
  truncated: z.boolean(),
  readAt: Instant,
});
export type OcDrillDown = z.infer<typeof OcDrillDownSchema>;

// Announcements ----------------------------------------------------------------------

export const AnnouncementPrioritySchema = z.enum(['normal', 'important']);
export type AnnouncementPriority = z.infer<typeof AnnouncementPrioritySchema>;

/**
 * S-M3 (D87): a promotion is an announcement of kind `promotion`: an attachment or image, a
 * place audience (sites, regions, the company) and a live window; priority normal, never
 * must-acknowledge, never in the banner. It shows in the Announcements card, on the Sales branch
 * page and on Home for people at those sites.
 */
export const AnnouncementKindSchema = z.enum(['announcement', 'promotion']);
export type AnnouncementKind = z.infer<typeof AnnouncementKindSchema>;
export const ANNOUNCEMENT_KIND_LABELS: Record<AnnouncementKind, string> = {
  announcement: 'Announcement',
  promotion: 'Promotion',
};

export const AnnouncementStateSchema = z.enum([
  'draft',
  'scheduled',
  'live',
  'expired',
  'withdrawn',
]);
export type AnnouncementState = z.infer<typeof AnnouncementStateSchema>;
export const ANNOUNCEMENT_STATE_LABELS: Record<AnnouncementState, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  live: 'Live',
  expired: 'Expired',
  withdrawn: 'Withdrawn',
};

/** Position kinds an announcement can target (positions.kind). */
export const AudiencePositionKindSchema = z.enum([
  'bm',
  'abm',
  'rm',
  'md',
  'director',
  'hr_lead',
  'fleet_lead',
]);
export type AudiencePositionKind = z.infer<typeof AudiencePositionKindSchema>;
export const AUDIENCE_POSITION_LABELS: Record<AudiencePositionKind, string> = {
  bm: 'Branch managers',
  abm: 'Assistant branch managers',
  rm: 'Regional managers',
  md: 'Managing director',
  director: 'Directors',
  hr_lead: 'HR leads',
  fleet_lead: 'Fleet leads',
};

/**
 * Places (company, or a union of divisions/regions/sites) AND who (everyone there, or only the
 * holders of these seat kinds / roles there).
 */
export const AudienceSchema = z.object({
  everyone: z.boolean(),
  divisionIds: z.array(z.uuid()).max(50),
  regionIds: z.array(z.uuid()).max(100),
  siteIds: z.array(z.uuid()).max(500),
  positionKinds: z.array(AudiencePositionKindSchema).max(10),
  roleCodes: z.array(z.string().min(1).max(40)).max(30),
});
export type Audience = z.infer<typeof AudienceSchema>;

export const AudiencePreviewSchema = z.object({
  count: z.number().int(),
  sites: z.number().int(),
  /** A few names, for a sanity check. */
  sample: z.array(z.string()),
  /** Human summary, e.g. "Branch managers in Severn". */
  label: z.string(),
});
export type AudiencePreview = z.infer<typeof AudiencePreviewSchema>;

export const AnnouncementWriteSchema = z.object({
  title: z.string().trim().min(3).max(200),
  kind: AnnouncementKindSchema.default('announcement'),
  /** Rich text HTML from the editor; sanitised on the server. */
  bodyHtml: z.string().max(100_000),
  priority: AnnouncementPrioritySchema,
  mustAcknowledge: z.boolean(),
  audience: AudienceSchema,
  /** ISO instant; null = when published. */
  publishAt: Instant.nullable(),
  expireAt: Instant.nullable(),
});
/** The input shape: `kind` defaults to `announcement` when a client leaves it out. */
export type AnnouncementWrite = z.input<typeof AnnouncementWriteSchema>;

export const AnnouncementStatsSchema = z.object({
  recipients: z.number().int(),
  read: z.number().int(),
  acknowledged: z.number().int(),
});
export type AnnouncementStats = z.infer<typeof AnnouncementStatsSchema>;

export const AnnouncementAdminSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  kind: AnnouncementKindSchema,
  bodyHtml: z.string(),
  priority: AnnouncementPrioritySchema,
  mustAcknowledge: z.boolean(),
  audience: AudienceSchema,
  audienceLabel: z.string(),
  publishAt: Instant.nullable(),
  expireAt: Instant.nullable(),
  state: AnnouncementStateSchema,
  image: FileSummarySchema.nullable(),
  attachment: FileSummarySchema.nullable(),
  createdBy: PersonRefSchema.nullable(),
  createdAt: Instant,
  updatedAt: Instant,
  publishedBy: PersonRefSchema.nullable(),
  deliveredAt: Instant.nullable(),
  withdrawnAt: Instant.nullable(),
  stats: AnnouncementStatsSchema,
});
export type AnnouncementAdmin = z.infer<typeof AnnouncementAdminSchema>;

export const AnnouncementsAdminResponseSchema = z.object({
  items: z.array(AnnouncementAdminSchema),
});
export type AnnouncementsAdminResponse = z.infer<typeof AnnouncementsAdminResponseSchema>;

/** What a recipient sees. */
export const AnnouncementViewSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  kind: AnnouncementKindSchema,
  bodyHtml: z.string(),
  /** First ~200 characters of the body as plain text, for cards. */
  excerpt: z.string(),
  priority: AnnouncementPrioritySchema,
  mustAcknowledge: z.boolean(),
  publishedAt: Instant,
  expireAt: Instant.nullable(),
  from: z.string(),
  image: FileSummarySchema.nullable(),
  attachment: FileSummarySchema.nullable(),
  readAt: Instant.nullable(),
  acknowledgedAt: Instant.nullable(),
});
export type AnnouncementView = z.infer<typeof AnnouncementViewSchema>;

export const MyAnnouncementsSchema = z.object({
  /** Live announcements for me: must-acknowledge-not-yet first, then unread, then newest. */
  items: z.array(AnnouncementViewSchema),
  unread: z.number().int(),
  toAcknowledge: z.number().int(),
  /** Important unread or must-acknowledge unacknowledged: the shell banner. */
  banner: z.array(AnnouncementViewSchema),
});
export type MyAnnouncements = z.infer<typeof MyAnnouncementsSchema>;

export const AnnouncementRecipientSchema = z.object({
  person: PersonRefSchema,
  site: SiteRefSchema.nullable(),
  addedAt: Instant,
  readAt: Instant.nullable(),
  acknowledgedAt: Instant.nullable(),
});
export type AnnouncementRecipient = z.infer<typeof AnnouncementRecipientSchema>;

export const AnnouncementReportSchema = z.object({
  announcement: AnnouncementAdminSchema,
  bySite: z.array(
    z.object({
      site: SiteRefSchema.nullable(),
      recipients: z.number().int(),
      read: z.number().int(),
      acknowledged: z.number().int(),
    }),
  ),
  recipients: z.array(AnnouncementRecipientSchema),
});
export type AnnouncementReport = z.infer<typeof AnnouncementReportSchema>;

export const AudienceOptionsSchema = z.object({
  divisions: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string() })),
  regions: z.array(
    z.object({ id: z.uuid(), code: z.string(), name: z.string(), divisionId: z.uuid().nullable() }),
  ),
  sites: z.array(
    z.object({ id: z.uuid(), code: z.string(), name: z.string(), regionId: z.uuid() }),
  ),
  positionKinds: z.array(z.object({ kind: AudiencePositionKindSchema, label: z.string() })),
  roles: z.array(z.object({ code: z.string(), name: z.string() })),
});
export type AudienceOptions = z.infer<typeof AudienceOptionsSchema>;

export const OperationsOkSchema = z.object({ ok: z.literal(true) });
